(() => {
  class BohoCartCountdown extends HTMLElement {
    static observedAttributes = [
      'minutes',
      'heading',
      'message',
      'expiry-message',
      'debug'
    ];

    constructor() {
      super();
      this._tickHandle = null;

      this._state = {
        cartToken: null,
        cartSignature: null,
        expiresAt: null,
        initialised: false,
        expired: false
      };

      this.attachShadow({ mode: 'open' });
      this.shadowRoot.innerHTML = `
        <style>
          :host { display: block; }
          .wrap {
            border: 1px solid rgba(0,0,0,.12);
            border-radius: 12px;
            padding: 12px 14px;
            background: var(--CountdownTimer_Background, rgba(0,0,0,.02));
            color: var(--CountdownTimer_Text, inherit);
          }
          .row {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: 12px;
          }
          .heading {
            font-weight: 600;
            margin: 0 0 6px 0;
            font-size: 14px;
          }
          .message {
            margin: 0;
            font-size: 13px;
            opacity: .85;
          }
          .time {
            font-variant-numeric: tabular-nums;
            font-weight: 700;
            letter-spacing: .2px;
            white-space: nowrap;
          }
          .hidden { display: none !important; }
        </style>

        <div class="wrap hidden" part="wrap">
          <div class="row">
            <div>
              <p class="heading" part="heading"></p>
              <p class="message" part="message"></p>
            </div>
            <div class="time" part="time"></div>
          </div>
        </div>
      `;

      this.$wrap = this.shadowRoot.querySelector('.wrap');
      this.$heading = this.shadowRoot.querySelector('.heading');
      this.$message = this.shadowRoot.querySelector('.message');
      this.$time = this.shadowRoot.querySelector('.time');
    }

    connectedCallback() {
      this._applyText();
      this._start();
    }

    disconnectedCallback() {
      this._stopTimers();
    }

    attributeChangedCallback() {
      if (!this.shadowRoot) return;
      this._applyText();

      // If minutes changes at runtime, keep current expiry unless cart changes.
      // If you want changing minutes to force reset, do it intentionally.
    }

    get minutes() {
      const raw = this.getAttribute('minutes');
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : 60;
    }

    get heading() {
      return this.getAttribute('heading') ?? '';
    }

    get message() {
      return this.getAttribute('message') ?? '';
    }

    get expiryMessage() {
      return this.getAttribute('expiry-message') ?? '';
    }

    get debug() {
      return this.hasAttribute('debug');
    }

    _applyText() {
      this.$heading.textContent = this.heading;
      this.$heading.classList.toggle('hidden', !this.heading);
      // message text is set in render depending on expired/non-expired
    }

    _start() {
      document.documentElement.addEventListener('cart:refresh', this._onCartRefresh);
      document.addEventListener('cart:refresh', this._onCartRefresh);
      window.addEventListener('cart:refresh', this._onCartRefresh);
      window.addEventListener('mousedown', this._onGlobalCapture, true);
      window.addEventListener('touchstart', this._onGlobalCapture, true);
      window.addEventListener('click', this._onGlobalCapture, true);
      this._startTick();
      this._syncFromCurrentDom({ reason: 'initial-dom' });
    }

    _stopTimers() {
      if (this._tickHandle) window.clearInterval(this._tickHandle);
      this._tickHandle = null;

      document.documentElement.removeEventListener('cart:refresh', this._onCartRefresh);
      document.removeEventListener('cart:refresh', this._onCartRefresh);
      window.removeEventListener('cart:refresh', this._onCartRefresh);
      window.removeEventListener('mousedown', this._onGlobalCapture, true);
      window.removeEventListener('touchstart', this._onGlobalCapture, true);
      window.removeEventListener('click', this._onGlobalCapture, true);
    }

    _onCartRefresh = (event) => {
      const payload = event?.detail?.cart;

      if (payload) {
        this._syncFromCartPayload(payload, { reason: 'event' });
        return;
      }

      this._syncFromCurrentDom({ reason: 'event-dom-fallback' });
    };

    _onGlobalCapture = (event) => {
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      if (path.includes(this)) {
        event.stopImmediatePropagation();
      }
    };

    _findCurrentCartPayload() {
      const localCartForm = this.closest('form[action*="/cart"]');
      if (localCartForm) return localCartForm;

      const miniCartForm = document.getElementById('mini-cart-form');
      if (miniCartForm) return miniCartForm;

      const cartForms = Array.from(document.querySelectorAll('form[action*="/cart"]'));
      return (
        cartForms.find((form) => form.querySelector('input[name="updates[]"][data-line]')) ||
        cartForms[0] ||
        null
      );
    }

    _syncFromCurrentDom({ reason }) {
      const payload = this._findCurrentCartPayload();
      if (!payload) {
        this._log('No cart DOM payload found', { reason });
        return;
      }

      this._syncFromCartPayload(payload, { reason });
    }

    _signatureFromCartItems(items) {
      const parts = items
        .map((item) => `${item.id}:${item.quantity}`)
        .sort();
      return parts.join('|');
    }

    _signatureFromCartMarkup(cartDoc) {
      const qtyInputs = Array.from(
        cartDoc.querySelectorAll('input[name="updates[]"][data-line]')
      );

      if (!qtyInputs.length) return null;

      const parts = qtyInputs
        .map((input) => {
          const line = input.getAttribute('data-line') || '0';
          const quantity = Number(input.value || 0);
          return `${line}:${Number.isFinite(quantity) ? quantity : 0}`;
        })
        .sort();

      return parts.join('|');
    }

    _normaliseCartPayload(cart) {
      if (!cart || typeof cart !== 'object') return null;

      // Standard Shopify cart.js payload.
      if (Array.isArray(cart.items) && Number.isFinite(Number(cart.item_count))) {
        return {
          cartToken: cart.token || null,
          itemCount: Number(cart.item_count || 0),
          cartSignature: this._signatureFromCartItems(cart.items)
        };
      }

      // Some theme flows dispatch a parsed HTML Document instead.
      if (cart instanceof Document || cart instanceof HTMLElement) {
        const cartSignature = this._signatureFromCartMarkup(cart);
        if (!cartSignature) return null;

        const qtyInputs = Array.from(
          cart.querySelectorAll('input[name="updates[]"][data-line]')
        );

        const itemCount = qtyInputs.reduce((count, input) => {
          const quantity = Number(input.value || 0);
          return count + (Number.isFinite(quantity) ? quantity : 0);
        }, 0);

        return {
          cartToken: this._state.cartToken || null,
          itemCount,
          cartSignature
        };
      }

      return null;
    }

    _storageKey(cartToken) {
      return `boho_cart_timer:${cartToken || 'no_token'}`;
    }

    _loadStoredState(cartToken) {
      const key = this._storageKey(cartToken);
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        return parsed;
      } catch {
        return null;
      }
    }

    _saveStoredState(cartToken, data) {
      const key = this._storageKey(cartToken);
      try {
        localStorage.setItem(key, JSON.stringify(data));
      } catch {
        // storage might be blocked; component still works for this session
      }
    }

    _clearStateForEmptyCart(cartToken) {
      this._state.cartToken = cartToken || null;
      this._state.cartSignature = null;
      this._state.expiresAt = null;
      this._state.expired = false;
      this._state.initialised = true;

      try {
        localStorage.removeItem(this._storageKey(cartToken));
      } catch {
        // ignore storage failures
      }
    }

    _resetTimer(cartToken, cartSignature) {
      const now = Date.now();
      const expiresAt = now + this.minutes * 60 * 1000;

      this._state.expiresAt = expiresAt;
      this._state.cartSignature = cartSignature;
      this._state.expired = false;

      this._saveStoredState(cartToken, {
        expiresAt,
        cartSignature
      });

      this._log('Timer reset', { expiresAt, cartSignature });
    }

    _syncFromCartPayload(cartPayload, { reason }) {
      const normalised = this._normaliseCartPayload(cartPayload);

      if (!normalised) {
        this._log('Ignored cart update with unsupported payload', { reason });
        return;
      }

      const { cartToken, itemCount, cartSignature } = normalised;
      const resolvedCartToken = cartToken || this._state.cartToken || null;

      if (itemCount <= 0) {
        this._clearStateForEmptyCart(resolvedCartToken);
        this.$wrap.classList.add('hidden');
        return;
      }

      const tokenChanged = this._state.cartToken && resolvedCartToken && this._state.cartToken !== resolvedCartToken;
      const stored = this._loadStoredState(resolvedCartToken);

      if (!stored || tokenChanged) {
        this._state.cartToken = resolvedCartToken;
        this._resetTimer(resolvedCartToken, cartSignature);
      } else {
        this._state.cartToken = resolvedCartToken;
        this._state.expiresAt = stored.expiresAt;
        this._state.cartSignature = stored.cartSignature;

        if (stored.cartSignature !== cartSignature) {
          this._log('Cart changed, resetting timer', { reason, from: stored.cartSignature, to: cartSignature });
          this._resetTimer(resolvedCartToken, cartSignature);
        }
      }

      if (!Number.isFinite(this._state.expiresAt)) {
        this._resetTimer(resolvedCartToken, cartSignature);
      }

      this.$wrap.classList.remove('hidden');
      this._state.initialised = true;
      this._render();
    }

    _startTick() {
      if (this._tickHandle) window.clearInterval(this._tickHandle);

      this._tickHandle = window.setInterval(() => {
        if (!this._state.initialised) return;
        this._render();
      }, 250);
    }

    _render() {
      const now = Date.now();
      const remainingMs = Math.max(0, (this._state.expiresAt || 0) - now);
      const expired = remainingMs <= 0;

      this._state.expired = expired;

      const totalSeconds = Math.floor(remainingMs / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      this.$time.textContent = expired
        ? '00:00'
        : `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

      this.$message.textContent = expired ? this.expiryMessage : this.message;
      this.$message.classList.toggle('hidden', !(expired ? this.expiryMessage : this.message));
    }

    _log(msg, data) {
      if (!this.debug) return;
      // eslint-disable-next-line no-console
      console.log(`[boho-cart-countdown] ${msg}`, data || '');
    }
  }

  if (!customElements.get('boho-cart-countdown')) {
    customElements.define('boho-cart-countdown', BohoCartCountdown);
  }
})();
